import createDependableResource from 'utils/zustand/createDependableResource'
import { useBoostableFarmsInfo } from 'state/farmBoost/useBoostableFarmsInfo'
import { useDeserializedFarms } from 'state/farms/useDeserializedFarms'
import { useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import { useActiveFarmsPeriodicFetching, useFinishedFarmsPeriodicFetching } from 'state/farms/farmsPeriodicFetching'
import { useActiveFarmsUserDataFetching, useFinishedFarmsUserDataFetching } from 'state/farms/useDeserializedFarmsUser'
import { immediatePromise } from 'utils/waitPromise'
import type { DeserializedFarm } from 'state/types'

interface ActiveOrUserBoostedFarms {
  activeOrUserBoostedDeserializedFarms: DeserializedFarm[]
}

const getActiveOrUserBoostedFarms = async (): Promise<ActiveOrUserBoostedFarms> => {
  const boostableFarmsInfo = useBoostableFarmsInfo.getState().value.boostableFarms
  const userBoostedFarmsIds = useUserBoostedFarmsInfos.getState().value.boostedFarmsIds

  const activeOrUserBoostedFarms = boostableFarmsInfo.filter(
    (farm) =>
      farm.farmId !== 0 &&
      !farm.configIsFinished &&
      (!farm.boostFarmExpired || userBoostedFarmsIds.includes(farm.farmId)),
  )

  await Promise.all<any>([
    useActiveFarmsPeriodicFetching.untilRefreshedOnce(),
    useActiveFarmsUserDataFetching.getState().getLoadedValue(),
    ...(activeOrUserBoostedFarms.some((f) => f.configIsFinished)
      ? [
          useFinishedFarmsPeriodicFetching.untilRefreshedOnce(),
          useFinishedFarmsUserDataFetching.getState().getLoadedValue(),
        ]
      : []),
  ])
  await immediatePromise()

  const { farmsV1FromPid, farmsV2FromPid } = useDeserializedFarms.getState().value

  return {
    activeOrUserBoostedDeserializedFarms: activeOrUserBoostedFarms.map(
      ({ farmId }) => farmsV1FromPid[farmId] ?? farmsV2FromPid[farmId],
    ),
  }
}

export const useActiveOrUserBoostedFarms = createDependableResource<ActiveOrUserBoostedFarms>({
  initValue: {
    activeOrUserBoostedDeserializedFarms: [],
  },
  getValue: getActiveOrUserBoostedFarms,
  dependResources: [useBoostableFarmsInfo, useUserBoostedFarmsInfos, useDeserializedFarms],
  dbgName: 'ActiveOrUserBoostedFarms',
})
